import { DateTime } from "luxon";
import { CategoryWithParts, ILeadPartsTableGlobalFilters, IPart, IPartImage, PartStatus } from './../../../../models/Part';
import { CameraIcon } from "@heroicons/react/24/outline";
import Modal from "../../../../layouts/Modal";
import ImageCarousel from "../../../shared/Images/ImageCarousel";
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import PriceFormat from "../../../../utils/priceFormat";
import { FormatType } from "../../../../models/EbayOffer";
import formatDateTime from './../../../../utils/formatDateTime';
import useVehicleCategoryPartsOptimized from "../../../../data/useVehicleCategoryPartsOptimized";
import SearchByFiltersModal, { SearchByOption } from "../../../shared/Tables/SearchByFiltersModal";
import LoadingPulse from "../../../shared/LoadingPulse";
import { ButtonInput } from "../../../shared/Inputs/ButtonInput";
import openInNewTab from "../../../../utils/openInNewTab";

function formatPartStatus(status: PartStatus): string {
  return PartStatus[status] ?? status.toString();
}

function daysBetween(from: string | null | undefined, to: string | null | undefined): number {
  if (!from || !to) return 0;

  const start = DateTime.fromISO(from).startOf("day");
  const end = DateTime.fromISO(to).startOf("day");
  return Math.max(0, Math.floor(end.diff(start, "days").days));
}

function PartTableRow({ part, variant, onOpenPictures, }: {
  part: IPart;
  variant: "currentStock" | "salesHistory";
  onOpenPictures: (images: IPartImage[]) => void;
}) {
  const { t } = useTranslation("leads");
  const imageUrl = part.images?.find((image) => image.isThumbnail === true)?.url;
  const vehicle = part.vehicle;

  const offer = part.offers?.[0];

  let price: number | null = part.price ?? null;

  if (offer && variant === "currentStock") {
    price = offer.format == FormatType.Auction ? offer.auctionStartPrice ?? 0 : offer.buyItNowPrice ?? 0;
  } else if (!offer && variant === "currentStock") {
    price = part.enteredPrice ?? 0;
  }

  const daysInStock = variant === "currentStock"
    ? daysBetween(part.created, DateTime.now().toISO() ?? part.created)
    : null;

  const daysSinceFirstListed = (variant === "currentStock" && (part.originalListedDate || part.listedDate || part.offers?.[0]?.publishedOn))
    ? daysBetween(part.originalListedDate ?? part.offers?.[0]?.publishedOn ?? part.listedDate, part.soldDate ?? DateTime.now().toISO())
    : null;

  const daysToSell = (variant === "salesHistory" && part.soldDate && part.offers?.[0]?.publishedOn)
    ? daysBetween(part.offers?.[0]?.publishedOn ?? "", part.soldDate)
    : null;

  return (
    <tr className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50/50">
      <td className="align-top p-2 w-[45%]">
        <div className="flex gap-2">
          <span
            onClick={() => onOpenPictures(part.images ?? [])}
            className="flex justify-center items-center w-20 h-20 rounded bg-gray-200 shrink-0 cursor-pointer overflow-hidden"
          >
            {imageUrl ? (
              <img className="w-full h-full object-contain" src={imageUrl} alt="" />
            ) : (
              <CameraIcon className="w-8 text-gray-500" />
            )}
          </span>
          <div className="flex flex-col text-sm min-w-0">
            <div><span className="text-gray-500">{t("Year:")}</span> {vehicle?.dvlaYearOfManufacture}</div>
            <div><span className="text-gray-500">{t("Make:")}</span> {vehicle?.combinedMake ?? "—"}</div>
            <div><span className="text-gray-500">{t("Model:")}</span> {vehicle?.combinedModel ?? "—"}</div>
            <div><span className="text-gray-500">{t("Reg:")}</span> {vehicle?.vrm ?? "—"}</div>
            <div><span className="text-gray-500">{t("Series:")}</span> {vehicle?.modelSeries ?? "—"}</div>
            <div><span className="text-gray-500">{t("Engine Code:")}</span> {vehicle?.engineModelCode ?? "—"}</div>
            <div><span className="text-gray-500">{t("Fuel Type:")}</span> {vehicle?.fuelType ?? "—"}</div>
            <div><span className="text-gray-500">{t("Body Style:")}</span> {vehicle?.bodyStyle ?? "—"}</div>
            <div><span className="text-gray-500">{t("Doors:")}</span> {vehicle?.numberOfDoors ?? "—"}</div>
          </div>
        </div>
      </td>
      <td className="align-top p-2 text-sm">
        {variant === "currentStock" && (
          <>
            <div><span className="text-gray-500">{t("Price:")}</span> {price ? PriceFormat(price ?? 0) : "—"}</div>
            <div><span className="text-gray-500">{t("Days in Stock:")}</span> {daysInStock ?? "—"}</div>
            <div><span className="text-gray-500">{t("Days Since First Listed:")}</span> {daysSinceFirstListed ?? "—"}</div>
            <div><span className="text-gray-500">{t("Part Number:")}</span> {part.partNumber ?? "—"}</div>
            <div><span className="text-gray-500">{t("Tag Number:")}</span> {part.tagNumber ?? "—"}</div>
            <div><span className="text-gray-500">{t("Part Status:")}</span> {t(formatPartStatus(part.partStatus))}</div>
            <div><span className="text-gray-500">{t("Warehouse Location:")}</span> {part.warehouseLocation?.displayName ?? "—"}</div>
          </>
        )}
        {variant === "salesHistory" && (
          <>
            <div><span className="text-gray-500">{t("Sold Price:")}</span> {price ? PriceFormat(price ?? 0) : "—"}</div>
            <div><span className="text-gray-500">{t("Sold Date:")}</span> {formatDateTime(part.soldDate ?? "")}</div>
            <div><span className="text-gray-500">{t("Days to Sell:")}</span> {daysToSell ?? "—"}</div>
            <div><span className="text-gray-500">{t("Part Number:")}</span> {part.partNumber ?? "—"}</div>
            <div><span className="text-gray-500">{t("Tag Number:")}</span> {part.tagNumber ?? "—"}</div>
          </>
        )}
      </td>
      <td>
        <div>
          <ButtonInput size="sm" label={t("View Stock")} isSubmit={false} onClick={() => openInNewTab(`/stock/${part.id}`)} classes={undefined} />
        </div>
      </td>
    </tr>
  );
}

export default function LeadPartsCategoriesTableSubRow({
  categoryWithParts,
  vehicleId,
  vehicleYear,
  vehicleVisibilityDate,
  vehicleTerminateDate,
  vehicleModelSeries,
  leadPartsTableFilters,
}: {
  categoryWithParts: CategoryWithParts;
  vehicleId: string;
  vehicleYear?: number | null;
  vehicleVisibilityDate?: string | null;
  vehicleTerminateDate?: string | null;
  vehicleModelSeries?: string | null;
  leadPartsTableFilters: ILeadPartsTableGlobalFilters;
}) {
  const { t } = useTranslation("leads");
  const {
    bodySearchBy: globalBodySearchBy,
    bodyYearFrom: globalBodyYearFrom,
    bodyYearTo: globalBodyYearTo,
    mechSearchBy: globalMechSearchBy,
    mechYearFrom: globalMechYearFrom,
    mechYearTo: globalMechYearTo,
    categoryStatusSearchBy: globalPartStatusSearchBy,
    searchVersion: globalSearchVersion,
  } = leadPartsTableFilters;

  const [partPicturesOpen, setPartPicturesOpen] = useState<boolean>(false);
  const [partPictures, setPartPictures] = useState<IPartImage[]>([]);

  // Treat invalid dual-flag categories (body+mech) as body so body matching rules are used.
  const isMech =
    categoryWithParts.category.mechOnly === true &&
    categoryWithParts.category.partOnly !== true;

  const filterOptions: SearchByOption[] = useMemo(() => {
    if (isMech) {
      return [
        { label: t("Match make"), value: "matchMake" },
        { label: t("Match model"), value: "matchModel" },
        { label: t("Match KType"), value: "matchKType" },
        { label: t("Match series"), value: "matchSeries" },
        { label: t("Match fuel type"), value: "matchFuelType" },
        { label: t("Match engine code"), value: "matchEngineCode" },
        { label: t("Match engine size"), value: "matchEngineSize" },
        { label: t("Match exact year"), value: "matchExactYear" },
        { label: t("Match year range"), value: "matchYearRange" },
      ];
    }

    return [
      { label: t("Match make"), value: "matchMake" },
      { label: t("Match model"), value: "matchModel" },
      { label: t("Match KType"), value: "matchKType" },
      { label: t("Match series"), value: "matchSeries" },
      { label: t("Match MK"), value: "matchMk" },
      { label: t("Match body style"), value: "matchBodyStyle" },
      { label: t("Match doors"), value: "matchDoors" },
      { label: t("Match exact year"), value: "matchExactYear" },
      { label: t("Match year range"), value: "matchYearRange" },
    ];
  }, [isMech, t]);

  const fallbackFilters = useMemo(() => {
    return isMech
      ? ["matchMake", "matchModel", "matchFuelType", "matchEngineCode", "matchEngineSize"]
      : ["matchMake", "matchModel", "matchBodyStyle", "matchDoors", "matchExactYear", "matchYearRange"];
  }, [isMech]);

  const getSelectedPartStatuses = (filters: string[]): PartStatus[] => {
    const statusOptionMap: Record<string, PartStatus> = {
      partStatusOnTheCar: PartStatus["On The Car"],
      partStatusPendingDismantle: PartStatus["Pending Dismantle"],
      partStatusDismantled: PartStatus.Dismantled,
      partStatusDamaged: PartStatus.Damaged,
      partStatusQc: PartStatus.QC,
      partStatusMissing: PartStatus.Missing,
      partStatusSold: PartStatus.Sold,
      partStatusDeleted: PartStatus.Deleted,
      partStatusReturned: PartStatus.Returned,
    };

    return filters
      .map((value) => statusOptionMap[value])
      .filter((status): status is PartStatus => typeof status !== "undefined");
  };

  const availableOptionValues = useMemo(
    () => new Set(filterOptions.map((opt) => opt.value)),
    [filterOptions]
  );

  const resolveFiltersForCategory = (incomingFilters?: string[]) => {
    const source = incomingFilters ?? fallbackFilters;
    return source.filter((value) => availableOptionValues.has(value));
  };

  const scopedGlobalSearchBy = isMech ? globalMechSearchBy : globalBodySearchBy;
  const scopedGlobalPartStatusFilters = globalPartStatusSearchBy ?? ["partStatusDismantled", "partStatusSold"];

  const [activeFilters, setActiveFilters] = useState<string[]>(() => {
    return resolveFiltersForCategory(scopedGlobalSearchBy);
  });

  useEffect(() => {
    setActiveFilters(resolveFiltersForCategory(scopedGlobalSearchBy));
  }, [isMech, categoryWithParts.category.id, globalSearchVersion]);

  const getDefaultYearRange = () => {
    const scopedGlobalYearFrom = isMech ? globalMechYearFrom : globalBodyYearFrom;
    const scopedGlobalYearTo = isMech ? globalMechYearTo : globalBodyYearTo;

    if (typeof scopedGlobalYearFrom !== "undefined" || typeof scopedGlobalYearTo !== "undefined") {
      return {
        from: scopedGlobalYearFrom,
        to: scopedGlobalYearTo,
      };
    }

    const fallbackYear = vehicleYear ?? undefined;
    const visibilityYear = vehicleVisibilityDate ? DateTime.fromISO(vehicleVisibilityDate).year : undefined;
    const terminateYear = vehicleTerminateDate ? DateTime.fromISO(vehicleTerminateDate).year : undefined;

    return {
      from: visibilityYear ?? fallbackYear,
      to: terminateYear ?? fallbackYear,
    };
  };

  const initialRange = getDefaultYearRange();
  const [yearFrom, setYearFrom] = useState<number | undefined>(initialRange.from);
  const [yearTo, setYearTo] = useState<number | undefined>(initialRange.to);
  const [draftYearFrom, setDraftYearFrom] = useState<number | undefined>(initialRange.from);
  const [draftYearTo, setDraftYearTo] = useState<number | undefined>(initialRange.to);

  useEffect(() => {
    const nextRange = getDefaultYearRange();
    setYearFrom(nextRange.from);
    setYearTo(nextRange.to);
    setDraftYearFrom(nextRange.from);
    setDraftYearTo(nextRange.to);
  }, [vehicleYear, vehicleVisibilityDate, vehicleTerminateDate, categoryWithParts.category.id, globalSearchVersion]);

  const { parts: categoryPartsQuery } = useVehicleCategoryPartsOptimized(
    vehicleId,
    categoryWithParts.category.id,
    {
      includeSold: true,
      limitSold: categoryWithParts.totalSold ?? 50,
      matchMake: activeFilters.includes("matchMake"),
      matchModel: activeFilters.includes("matchModel"),
      matchKType: activeFilters.includes("matchKType"),
      matchSeries: activeFilters.includes("matchSeries") && !!vehicleModelSeries,
      matchMk: activeFilters.includes("matchMk"),
      matchBodyStyle: activeFilters.includes("matchBodyStyle"),
      matchDoors: activeFilters.includes("matchDoors"),
      matchFuelType: activeFilters.includes("matchFuelType"),
      matchEngineSize: activeFilters.includes("matchEngineSize"),
      matchExactYear: activeFilters.includes("matchExactYear"),
      matchYearRange: activeFilters.includes("matchYearRange"),
      yearFrom: yearFrom,
      yearTo: yearTo,
      matchEngineCode: activeFilters.includes("matchEngineCode"),
      partStatuses: getSelectedPartStatuses(scopedGlobalPartStatusFilters),
    }
  );
  const isLoadingParts = categoryPartsQuery.isLoading || categoryPartsQuery.isFetching;

  const { currentStock, salesHistory } = useMemo(() => {
    const hasServerData = Array.isArray(categoryPartsQuery.data?.data);
    const parts: IPart[] = hasServerData
      ? (categoryPartsQuery.data?.data as IPart[])
      : (isLoadingParts ? [] : (categoryWithParts.parts ?? []));
    const currentStock = parts.filter((p: IPart) => !p.soldDate);
    const salesHistory = parts
      .filter((p: IPart) => p.soldDate)
      .sort((a: IPart, b: IPart) => {
        const aDate = a.isInternal && a.soldDate
          ? DateTime.fromISO(a.soldDate).toMillis()
          : DateTime.fromISO(a.created).toMillis();
        const bDate = b.isInternal && b.soldDate
          ? DateTime.fromISO(b.soldDate).toMillis()
          : DateTime.fromISO(b.created).toMillis();
        return bDate - aDate;
      });
    return { currentStock, salesHistory };
  }, [categoryWithParts.parts, categoryPartsQuery.data?.data, isLoadingParts]);

  const openPictures = (images: IPartImage[]) => {
    setPartPictures(images);
    setPartPicturesOpen(true);
  };

  return (
    <>
      <Modal open={partPicturesOpen} setOpen={setPartPicturesOpen} width={"max-w-7xl"}>
        <ImageCarousel images={partPictures} part />
      </Modal>

      <div className="flex items-center justify-between px-4 pt-4">
        <div className="text-sm font-semibold text-gray-700">
          {t("Matching rules ({{type}})", { type: isMech ? t("Mech") : t("Body") })}
        </div>
        <SearchByFiltersModal
          options={filterOptions}
          searchBy={activeFilters}
          setSearchBy={setActiveFilters}
          isLoading={isLoadingParts}
          applyOnClose={true}
          disabled={isLoadingParts}
          modalWidth="sm:max-w-lg"
          onOpenChange={(open) => {
            if (open) {
              setDraftYearFrom(yearFrom);
              setDraftYearTo(yearTo);
              return;
            }
            setYearFrom(draftYearFrom);
            setYearTo(draftYearTo);
          }}
          extraContent={
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-900">{t("Year range")}</div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  min={1900}
                  max={2100}
                  value={draftYearFrom ?? ""}
                  onChange={(e) => setDraftYearFrom(e.target.value === "" ? undefined : Number(e.target.value))}
                  disabled={isLoadingParts}
                  className="w-full rounded-md border-gray-300 text-sm disabled:bg-gray-100"
                  placeholder={t("From")}
                />
                <input
                  type="number"
                  min={1900}
                  max={2100}
                  value={draftYearTo ?? ""}
                  onChange={(e) => setDraftYearTo(e.target.value === "" ? undefined : Number(e.target.value))}
                  disabled={isLoadingParts}
                  className="w-full rounded-md border-gray-300 text-sm disabled:bg-gray-100"
                  placeholder={t("To")}
                />
              </div>
            </div>
          }
        />
      </div>

      <div key={categoryWithParts.category.id} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 pt-2">
        <div className="flex flex-col rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm">
          <div className="bg-blue-600 text-white font-semibold uppercase tracking-wide text-center py-2 px-3">
            {t("Current Stock")}
          </div>
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-100 z-10">
                <tr>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider p-2 border-b border-gray-200">
                    {t("Donor Vehicle")}
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider p-2 border-b border-gray-200">
                    {t("Stats")}
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider p-2 border-b border-gray-200">

                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoadingParts ? (
                  <tr>
                    <td colSpan={2} className="p-4 text-sm text-gray-500 text-center">
                      <LoadingPulse width="w-full" />
                    </td>
                  </tr>
                ) : currentStock.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="p-4 text-sm text-gray-500 text-center">
                      {t("No current stock")}
                    </td>
                  </tr>
                ) : (
                  currentStock.map((part: IPart) => (
                    <PartTableRow
                      key={part.id}
                      part={part}
                      variant="currentStock"
                      onOpenPictures={openPictures}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm">
          <div className="bg-green-600 text-white font-semibold uppercase tracking-wide text-center py-2 px-3">
            {t("Sales History")}
          </div>
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-100 z-10">
                <tr>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider p-2 border-b border-gray-200">
                    {t("Donor Vehicle")}
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider p-2 border-b border-gray-200">
                    {t("Stats")}
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider p-2 border-b border-gray-200">

                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoadingParts ? (
                  <tr>
                    <td colSpan={2} className="p-4 text-sm text-gray-500 text-center">
                      <LoadingPulse width="w-full" />
                    </td>
                  </tr>
                ) : salesHistory.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="p-4 text-sm text-gray-500 text-center">
                      {t("No sales history")}
                    </td>
                  </tr>
                ) : (
                  salesHistory.map((part: IPart) => (
                    <PartTableRow
                      key={part.id}
                      part={part}
                      variant="salesHistory"
                      onOpenPictures={openPictures}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}